'use client';

import clsx from 'clsx';
import Image from 'next/image';
import type { FieldValues } from 'react-hook-form';
import type { TextAreaProps } from '../types';
import { getErrorMessage } from '../utils/getErrorMessage';
import { labelStyles, textAreaErrorMessageStyles, textAreaStyles } from './styles';

export const TextArea = <T extends FieldValues>({
  label,
  name,
  errors,
  endIcon,
  startIcon,
  size,
  labelStyle,
  required,
  className,
  maxLength,
  errorMessagePosition,
  disableResize,
  footer,
  ...rest
}: TextAreaProps<T>) => {
  const errorMessage = getErrorMessage<T>(errors, name);
  const intent = rest.intent;
  const currentLength = typeof rest.value === 'string' ? rest.value?.length : 0;
  const isOverLimit = maxLength && currentLength > maxLength;

  return (
    <div className={clsx(className, 'relative')}>
      {(label || maxLength) && (
        <div className="mb-1 flex items-center justify-between">
          {label && (
            <label htmlFor={name} className={labelStyles({ ...labelStyle, required })}>
              {label}
            </label>
          )}
        </div>
      )}
      <div
        className={clsx(
          'relative w-full flex flex-col',
          maxLength && 'rounded-[6px] border border-grey-400',
          (!!errorMessage || isOverLimit) && '!border-red-500'
        )}
      >
        <div className="relative w-full flex items-center min-h-[100px] flex-1">
          {intent === 'search' && !startIcon && (
            <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none z-10">
              <Image src="/icons/search.svg" width={16} height={16} alt="Search" />
            </div>
          )}
          {startIcon && <div className="absolute left-4 top-1/2 -translate-y-1/2 z-10">{startIcon}</div>}

          <textarea
            name={name}
            className={textAreaStyles({
              intent,
              size,
              disabled: rest.disabled,
              error: !!errorMessage || Boolean(isOverLimit),
              disableResize,
              withCounter: !!maxLength,
            })}
            {...rest}
          />

          {endIcon && <div className="absolute right-4 top-1/2 -translate-y-1/2 z-10">{endIcon}</div>}
        </div>
        {maxLength && (
          <div className="flex justify-end pr-3 pb-2 pt-0 flex-shrink-0 bg-white rounded-b-[6px]">
            <span className={clsx('text-button-regular', isOverLimit ? 'text-red-550' : 'text-grey-700')}>
              {currentLength}/{maxLength}
            </span>
          </div>
        )}
        {footer}
      </div>
      {errorMessage && <p className={textAreaErrorMessageStyles({ position: errorMessagePosition })}>{errorMessage}</p>}
    </div>
  );
};
